import { Button } from '@/shared/ui/button';
import { useSearch } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, Building2, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/use-auth';

export function LoginForm() {
  const { login } = useAuth();
  const search = useSearch({ strict: false });
  const errorParam = (search as { error?: string })?.error;

  return (
    <div className="relative flex min-h-screen min-w-screen items-center justify-center overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/20" />
        <div className="absolute left-1/4 top-1/4 size-96 animate-pulse rounded-full bg-[#65211a]/5 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 size-96 animate-pulse rounded-full bg-[#65211a]/5 blur-3xl delay-1000" />

        {/* Floating Dollar Signs */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Left side */}
          <div className="absolute left-[5%] top-[10%] animate-float-1 text-7xl font-bold text-muted-foreground/20">
            $
          </div>
          <div className="absolute left-[8%] top-[35%] animate-float-2 text-5xl font-bold text-muted-foreground/15">
            $
          </div>
          <div className="absolute left-[3%] top-[60%] animate-float-3 text-8xl font-bold text-muted-foreground/20">
            $
          </div>
          <div className="absolute left-[12%] bottom-[15%] animate-float-4 text-6xl font-bold text-muted-foreground/15">
            $
          </div>

          {/* Right side */}
          <div className="absolute right-[5%] top-[8%] animate-float-4 text-6xl font-bold text-muted-foreground/15">
            $
          </div>
          <div className="absolute right-[10%] top-[30%] animate-float-1 text-9xl font-bold text-muted-foreground/25">
            $
          </div>
          <div className="absolute right-[6%] top-[55%] animate-float-2 text-5xl font-bold text-muted-foreground/15">
            $
          </div>
          <div className="absolute right-[8%] bottom-[12%] animate-float-3 text-7xl font-bold text-muted-foreground/20">
            $
          </div>

          {/* Top center */}
          <div className="absolute left-[30%] top-[5%] animate-float-4 text-5xl font-bold text-muted-foreground/15">
            $
          </div>
          <div className="absolute right-[35%] top-[8%] animate-float-1 text-6xl font-bold text-muted-foreground/15">
            $
          </div>

          {/* Bottom center */}
          <div className="absolute left-[35%] bottom-[8%] animate-float-3 text-8xl font-bold text-muted-foreground/25">
            $
          </div>
          <div className="absolute right-[30%] bottom-[10%] animate-float-2 text-5xl font-bold text-muted-foreground/15">
            $
          </div>

          {/* Mid section */}
          <div className="absolute left-[18%] top-[50%] animate-float-4 text-6xl font-bold text-muted-foreground/20">
            $
          </div>
          <div className="absolute right-[20%] top-[45%] animate-float-1 text-7xl font-bold text-muted-foreground/20">
            $
          </div>
        </div>
      </div>

      <div className="relative w-full max-w-[480px] px-4">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex items-center gap-2.5 rounded-xl bg-[#65211a] px-5 py-2.5 shadow-lg shadow-[#65211a]/20">
            <Building2 className="size-5 text-white" />
            <span className="text-sm font-bold uppercase tracking-wider text-white">Invisible</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-xl shadow-2xl">
          <div className="p-8 sm:p-10">
            {/* Header */}
            <div className="mb-8 space-y-2 text-center">
              <h1 className="text-3xl font-bold tracking-tight">Welcome to Finance</h1>
              <p className="text-muted-foreground">Sign in to access your financial platform</p>
            </div>

            {/* Error Alert */}
            {errorParam && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-destructive">Authentication failed</p>
                  <p className="text-xs text-destructive/80">
                    {errorParam === 'auth_failed'
                      ? 'Unable to sign in. Please try again.'
                      : 'An unexpected error occurred during login.'}
                  </p>
                </div>
              </div>
            )}

            {/* Sign In Button */}
            <Button
              onClick={login}
              size="lg"
              className="group relative w-full overflow-hidden bg-[#65211a] text-base font-semibold text-white shadow-lg shadow-[#65211a]/25 transition-all hover:bg-[#4a1813] hover:shadow-xl hover:shadow-[#65211a]/30"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <svg className="size-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                </svg>
                Continue with Google
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">Secure authentication</span>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid gap-3">
              <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#65211a]/10">
                  <Sparkles className="size-4 text-[#65211a]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Enterprise-grade security</p>
                  <p className="text-xs text-muted-foreground">Protected by Auth0 authentication</p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#65211a]/10">
                  <svg
                    className="size-4 text-[#65211a]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">End-to-end encryption</p>
                  <p className="text-xs text-muted-foreground">Your data is always protected</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-border/50 bg-muted/20 px-8 py-5 text-center sm:px-10">
            <p className="text-xs text-muted-foreground">
              By signing in, you agree to our{' '}
              <button type="button" className="font-medium text-[#65211a] hover:underline">
                Terms of Service
              </button>{' '}
              and{' '}
              <button type="button" className="font-medium text-[#65211a] hover:underline">
                Privacy Policy
              </button>
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            © 2025 Invisible Finance. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
